import * as React from "react";
import {ListSubheader, List as MuiList, useMediaQuery, useTheme} from "@mui/material";
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import {styled} from "@mui/material";
import { Box } from "@mui/system";

const StyledMuiList = styled(MuiList, {
    shouldForwardProp: (prop) => prop !== 'width',
})<{width?: number}>(({width = DEFAULT_MAX_WIDTH, theme}) => ({
    [theme.breakpoints.up('lg')]: {
        maxWidth: width,
    },
}));

export const StyledListItem = styled(ListItem)(({theme}) => ({
    [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}));

const DEFAULT_MAX_WIDTH = 600;
const EMPTY_ACTIONS = (_: any) => [];

interface IListProps<T> {
    items: T[];
    primaryGetter?(item: T): string | React.ReactNode;
    secondaryGetter?(item: T): string | React.ReactNode;
    iconGetter?(item: T): any;
    groupBy?(item: T): any;
    groupHeaderLabel?(item: T): string;
    width?: number;
    actions?(item: T, index: number): React.ReactElement[] | any[] | any[][];
    onClick?(item: T): () => void;
}

export const List = <T extends object>({
    items,
    primaryGetter,
    secondaryGetter,
    iconGetter,
    groupBy,
    groupHeaderLabel,
    width = DEFAULT_MAX_WIDTH,
    actions = EMPTY_ACTIONS,
    onClick,
}: IListProps<T>) => {
    const dense = useMediaQuery(useTheme().breakpoints.down('sm'));

    const renderListItem = (item: T, index: number) => {
        const primary = !!primaryGetter ? primaryGetter(item) : undefined;
        const secondary = !!secondaryGetter ? secondaryGetter(item) : undefined;
        const icon = !!iconGetter ? iconGetter(item) : undefined;

        return (
            <StyledListItem
                key={index}
                secondaryAction={(
                    <Box sx={{marginRight:-2, padding: 0}}>
                        {actions(item, index).map((action, actionIndex) => {
                            let Action: React.ReactElement | any;
                            let props: any = {};
                            if (Array.isArray(action) && action.length > 1) {
                                [Action, props] = action;
                            } else {
                                Action = action;
                            }
                            return (
                                <Action key={actionIndex} item={item} {...props} />
                            );
                        })}
                    </Box>
                )}
            >
                <ListItemAvatar>
                    <Avatar>
                        {icon}
                    </Avatar>
                </ListItemAvatar>

                <ListItemText
                    primary={primary}
                    secondary={secondary}
                    slotProps={{
                        primary: !!onClick ? {
                            sx: (theme) => ({
                                cursor: 'pointer',
                                '&:hover': { color: theme.palette.primary.main },
                                [theme.breakpoints.down('sm')]: { maxWidth: '75%' }
                            })
                        } : {},
                        secondary: !!onClick ? {
                            sx: (theme) => ({
                                [theme.breakpoints.down('sm')]: { maxWidth: '75%' }
                            })
                        } : {},
                    }}
                    onClick={!!onClick ? onClick(item) : undefined}
                />
            </StyledListItem>
        );
    };

    if (!!groupBy) {
        const groups = items.reduce((acc: Record<string, T[]>, item) => {
            const groupKey = groupBy(item);
            if (!acc[groupKey]) acc[groupKey] = [];
            acc[groupKey].push(item);
            return acc;
        }, {});

        return (
            <StyledMuiList dense={dense} width={width}>
                {Object.entries(groups).map(([groupKey, groupItems]) => (
                    <Box sx={{marginBottom: 4, '&:last-of-type': {marginBottom: 0}}} key={groupKey}>
                        <ListSubheader sx={{backgroundColor: 'transparent', lineHeight: 1.5}} component="div">
                            {groupHeaderLabel ? groupHeaderLabel(groupItems[0]) : groupKey}
                        </ListSubheader>
                        {groupItems.map(renderListItem)}
                    </Box>
                ))}
            </StyledMuiList>
        );
    }

    return (
        <StyledMuiList dense={dense} width={width}>
            {items.map(renderListItem)}
        </StyledMuiList>
    );
};
